import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import HttpService from '../../../services/httpService';
import { setLoading, setSnackInfo } from '../../../redux/actions/appAction';

const defaultForm = {
  name: '',
};

function KekaDepartments(props) {
  const [rows, setRows] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [editId, setEditId] = useState('');
  const [deleteId, setDeleteId] = useState('');
  const [form, setForm] = useState(defaultForm);

  const loadDepartments = async () => {
    props.setLoading(true);
    try {
      const response = await HttpService.getKekaDepartments();
      const list = Array.isArray(response?.data) ? response.data : [];
      setRows(list);
    } catch (error) {
      props.setSnackInfo(
        error?.response?.data?.message || 'Error loading keka departments',
        'error'
      );
      setRows([]);
    } finally {
      props.setLoading(false);
    }
  };

  useEffect(() => {
    void loadDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenCreate = () => {
    setEditId('');
    setForm(defaultForm);
    setOpenForm(true);
  };

  const handleOpenEdit = (item) => {
    setEditId(item?._id || '');
    setForm({
      name: item?.name || '',
    });
    setOpenForm(true);
  };

  const validateForm = () => {
    if (!String(form.name || '').trim()) return 'Department name is required';
    return '';
  };

  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      props.setSnackInfo(validationError, 'error');
      return;
    }

    props.setLoading(true);
    try {
      const payload = {
        name: String(form.name || '').trim(),
      };
      if (editId) {
        await HttpService.updateKekaDepartment(editId, payload);
        props.setSnackInfo('Keka department updated successfully', 'success');
      } else {
        await HttpService.createKekaDepartment(payload);
        props.setSnackInfo('Keka department created successfully', 'success');
      }
      setOpenForm(false);
      setEditId('');
      setForm(defaultForm);
      await loadDepartments();
    } catch (error) {
      props.setSnackInfo(
        error?.response?.data?.message || 'Error saving keka department',
        'error'
      );
    } finally {
      props.setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    props.setLoading(true);
    try {
      await HttpService.deleteKekaDepartment(deleteId);
      props.setSnackInfo('Keka department deactivated', 'success');
      setOpenDelete(false);
      setDeleteId('');
      await loadDepartments();
    } catch (error) {
      props.setSnackInfo(
        error?.response?.data?.message || 'Error deleting keka department',
        'error'
      );
    } finally {
      props.setLoading(false);
    }
  };

  return (
    <Box p={2}>
      <Box
        mb={2}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="h6" style={{ fontWeight: 700 }}>
          Keka Departments
        </Typography>
        <Button
          variant="contained"
          style={{ backgroundColor: '#F47E46', color: '#fff' }}
          onClick={handleOpenCreate}
        >
          Add Keka Department
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Sr. No.</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  No keka departments found
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow key={row?._id || index}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{row?.name || '-'}</TableCell>
                  <TableCell>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEdit(row)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        style={{ color: '#d32f2f' }}
                        onClick={() => {
                          setDeleteId(row?._id || '');
                          setOpenDelete(true);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editId ? 'Edit Keka Department' : 'Add Keka Department'}
        </DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <TextField
              label="Department Name"
              variant="outlined"
              size="small"
              fullWidth
              value={form.name}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, name: e.target.value }))
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)} color="default">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            style={{ backgroundColor: '#F47E46', color: '#fff' }}
          >
            {editId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Keka Department</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to deactivate this department?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)} color="default">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="secondary" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default connect(null, { setLoading, setSnackInfo })(KekaDepartments);
